import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { X, ArrowRight, Leaf, MessageCircle, Search, CheckCircle2, CalendarDays } from "lucide-react";
import { MENU_CATEGORIES, MENU_ITEMS, waLink } from "@/data/site";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EVENT_TYPES = ["All", "Wedding", "Party", "Corporate", "Social"];
const TIERS = ["All", "Classic", "Premium", "Royal"];
const DIETS = ["All", "Jain"];

const Pill = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`px-4 py-2 text-xs tracking-[0.15em] uppercase border transition-colors duration-300 ${
      active ? "border-gold bg-gold text-espresso" : "border-cream/25 text-cream/70 hover:border-gold/60 hover:text-gold"
    }`}
  >
    {children}
  </button>
);

const MenuBrowser = ({ onClose, initialCat = "All" }) => {
  const [cat, setCat] = useState(initialCat);
  const [event, setEvent] = useState("All");
  const [tier, setTier] = useState("All");
  const [diet, setDiet] = useState("All");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState([]);
  const [date, setDate] = useState("");

  const items = useMemo(
    () =>
      MENU_ITEMS.filter(
        (m) =>
          (cat === "All" || m.cat === cat) &&
          (event === "All" || m.events.includes(event)) &&
          (tier === "All" || m.tier === tier) &&
          (diet === "All" || m.jain) &&
          (!query || `${m.name} ${m.desc} ${m.cat}`.toLowerCase().includes(query.toLowerCase()))
      ),
    [cat, event, tier, diet, query]
  );

  const toggle = (name) =>
    setSelected((s) => (s.includes(name) ? s.filter((x) => x !== name) : [...s, name]));

  const sendToWhatsApp = () => {
    const msg =
      `Hi Momay Caterers, I'd like to book catering.\n\n` +
      `Event date: ${date || "To be decided"}\n\n` +
      `My selected menu items (${selected.length}):\n` +
      selected.map((s, i) => `${i + 1}. ${s}`).join("\n") +
      `\n\nPlease share packages and availability.`;
    window.open(waLink(msg), "_blank", "noopener");
    axios
      .post(`${API}/enquiries`, {
        name: "Menu Builder",
        phone: "",
        event_type: event !== "All" ? event : "Menu Builder",
        event_date: date,
        message: `Selected ${selected.length} items: ${selected.join(", ")}`,
        source: "menu_builder",
      })
      .catch(() => {});
  };

  return (
    <motion.div
      data-testid="menu-browser"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] bg-espresso grain flex flex-col"
    >
      <div className="relative z-10 flex items-center justify-between px-5 md:px-10 h-16 md:h-20 border-b gold-hairline shrink-0">
        <div>
          <span className="font-serif text-xl md:text-2xl text-cream tracking-wide">{cat === "All" ? "Build Your Menu" : cat}</span>
          <span className="hidden md:inline text-cream/40 text-xs tracking-[0.2em] uppercase ml-4">
            {items.length} dishes · {selected.length} selected
          </span>
          {cat !== "All" && (
            <button data-testid="menu-all-cuisines" onClick={() => setCat("All")} className="hidden md:inline text-gold text-xs tracking-[0.2em] uppercase ml-4 underline underline-offset-4 decoration-gold/40 hover:text-cream transition-colors">
              View all cuisines
            </button>
          )}
        </div>
        <button data-testid="menu-browser-close" onClick={onClose} aria-label="Close menus" className="text-cream/70 hover:text-gold transition-colors">
          <X size={26} />
        </button>
      </div>

      <div className="relative z-10 px-5 md:px-10 py-5 border-b gold-hairline space-y-4 shrink-0 overflow-x-auto">
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-gold" />
          <input
            data-testid="menu-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search any dish — paneer, dosa, jalebi…"
            className="w-full bg-transparent border-b border-cream/25 focus:border-gold outline-none py-2.5 pl-7 text-cream placeholder:text-cream/35 font-light transition-colors duration-300"
          />
        </div>
        {cat !== "All" && (
          <button data-testid="menu-all-cuisines-mobile" onClick={() => setCat("All")} className="md:hidden text-gold text-xs tracking-[0.2em] uppercase underline underline-offset-4 decoration-gold/40">
            ← View all cuisines
          </button>
        )}
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-[10px] tracking-[0.3em] uppercase text-gold w-20">Event</span>
          {EVENT_TYPES.map((e) => (
            <Pill key={e} testid={`filter-event-${e.toLowerCase()}`} active={event === e} onClick={() => setEvent(e)}>{e}</Pill>
          ))}
        </div>
        <div className="flex flex-wrap gap-6">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-[10px] tracking-[0.3em] uppercase text-gold w-20">Budget</span>
            {TIERS.map((t) => (
              <Pill key={t} testid={`filter-budget-${t.toLowerCase()}`} active={tier === t} onClick={() => setTier(t)}>{t}</Pill>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-[10px] tracking-[0.3em] uppercase text-gold w-20">Diet</span>
            {DIETS.map((d) => (
              <Pill key={d} testid={`filter-diet-${d.toLowerCase()}`} active={diet === d} onClick={() => setDiet(d)}>{d === "All" ? "Vegetarian" : d}</Pill>
            ))}
          </div>
        </div>
      </div>

      <div className="relative z-10 flex-1 overflow-y-auto px-5 md:px-10 py-8 pb-32">
        {items.length === 0 ? (
          <p className="text-cream/50 text-center py-20 font-light" data-testid="menu-empty">No dishes match — try a different search or widen your filters.</p>
        ) : (
          <div className="max-w-5xl mx-auto divide-y divide-cream/10">
            {items.map((m) => {
              const isSel = selected.includes(m.name);
              return (
                <button
                  key={m.name}
                  data-testid={`menu-item-${m.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => toggle(m.name)}
                  className={`w-full text-left py-4 flex items-center justify-between gap-4 group transition-colors duration-300 ${isSel ? "text-gold" : ""}`}
                >
                  <div className="flex items-center gap-4">
                    <span className={`shrink-0 w-6 h-6 rounded-full border flex items-center justify-center transition-colors duration-300 ${isSel ? "border-gold bg-gold text-espresso" : "border-cream/25 text-transparent group-hover:border-gold/60"}`}>
                      <CheckCircle2 size={15} />
                    </span>
                    <div>
                      <span className={`font-serif text-xl md:text-2xl transition-colors duration-300 ${isSel ? "text-gold" : "text-cream group-hover:text-gold"}`}>{m.name}</span>
                      {m.jain && <Leaf size={13} className="inline ml-2 text-gold" aria-label="Jain option" />}
                      {m.desc && <p className="text-cream/50 text-sm font-light mt-1">{m.desc}</p>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="block text-[10px] tracking-[0.2em] uppercase text-gold/80">{m.tier}</span>
                    <span className="block text-[10px] tracking-[0.15em] uppercase text-cream/40 mt-1">{m.cat}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="relative z-10 px-5 md:px-10 py-4 border-t gold-hairline shrink-0 bg-espresso">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-stretch md:items-center gap-3 md:gap-5">
          <div className="relative flex-1 min-w-0">
            <CalendarDays size={15} className="absolute left-0 top-1/2 -translate-y-1/2 text-gold" />
            <input
              data-testid="menu-booking-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full bg-transparent border-b border-cream/25 focus:border-gold outline-none py-2.5 pl-7 text-cream font-light [color-scheme:dark] transition-colors duration-300"
            />
            <span className="absolute right-0 top-1/2 -translate-y-1/2 text-[9px] tracking-[0.25em] uppercase text-cream/40 pointer-events-none">Event date</span>
          </div>
          <p className="text-cream/50 text-xs font-light md:w-40" data-testid="menu-selected-count">
            {selected.length === 0 ? "Tap dishes to select them" : `${selected.length} dish${selected.length > 1 ? "es" : ""} selected`}
          </p>
          <button
            data-testid="menu-send-whatsapp"
            onClick={sendToWhatsApp}
            disabled={selected.length === 0}
            className="inline-flex items-center justify-center gap-3 bg-gold text-espresso px-8 py-4 text-xs tracking-[0.25em] uppercase font-medium hover:bg-cream transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <MessageCircle size={16} /> Send My Menu on WhatsApp
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export const MenuShowcase = () => {
  const [open, setOpen] = useState(false);
  const [initialCat, setInitialCat] = useState("All");

  useEffect(() => {
    const handler = () => { setInitialCat("All"); setOpen(true); };
    window.addEventListener("open-menu-builder", handler);
    return () => window.removeEventListener("open-menu-builder", handler);
  }, []);

  const openCategory = (name) => { setInitialCat(name); setOpen(true); };

  return (
    <section id="menus" data-testid="menu-section" className="bg-cream py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <SectionHeading chapter="04" eyebrow="Food & Menus" title="A Menu Made" script="around you." />
          <Reveal delay={0.15}>
            <p className="max-w-sm text-espresso/60 font-light text-sm md:text-base leading-relaxed">
              From traditional favourites to modern favourites, create a menu your guests will remember.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8">
          {MENU_CATEGORIES.map((c, i) => (
            <Reveal key={c.name} delay={(i % 3) * 0.08} className={i === 0 || i === 8 ? "lg:col-span-1" : ""}>
              <button
                data-testid={`menu-category-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => openCategory(c.name)}
                className="group relative block w-full overflow-hidden aspect-square text-left"
              >
                <img
                  src={c.img}
                  alt={`${c.name} catering menu by Momay Caterers`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-[1.2s] ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-espresso/90 via-espresso/25 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
                  <h3 className="font-serif text-xl md:text-2xl text-cream">{c.name}</h3>
                  <p className="text-cream/60 text-xs md:text-sm font-light mt-1">{c.blurb}</p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-14 text-center">
          <button
            data-testid="view-menus-btn"
            onClick={() => openCategory("All")}
            className="group inline-flex items-center gap-3 bg-espresso text-cream px-10 py-5 text-xs md:text-sm tracking-[0.3em] uppercase hover:bg-gold hover:text-espresso transition-colors duration-300"
          >
            View Our Menus
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </Reveal>
      </div>

      {open && <MenuBrowser key={initialCat} initialCat={initialCat} onClose={() => setOpen(false)} />}
    </section>
  );
};
